import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Form,
  Col,
  Row,
  Spin,
  Button,
  Space,
  DatePicker,
  Select,
  Typography,
  Image,
  message,
  Popconfirm,
  Upload,
  Checkbox,
  Alert
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { colors } from "../../../utils/colors";
import { dateFormat } from "../../../utils/helpers";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import { BASE_URL } from '../../../../Constants/EnvironmentVariableProvider';
import { RoutesLookup } from '../../../../Routes/RoutesLookup';
import {
  UPLOAD_WELCOME_IMAGE,
  GET_IMAGE_BY_ID,
  DELETE_WELCOME_IMAGE,
  GET_ALL_IMAGE_USAGES
} from '../../../../API/paths';
import { DATAGRID_DEFAULT_PAGE_SIZE } from '../../../../Constants/ApplicationConstants';
import uploadIcon from "../../../../../assets/icons/uploadIcon.svg";

const { Option } = Select;
const { Text } = Typography;
const { Dragger } = Upload;

const GlobalImageForm = ({
  id,
  form,
  handleSubmit,
  loading,
  statusType,
  returnPage,
  returnPageSize,
}) => {
  const navigate = useNavigate();
  const [imageUsages, setImageUsages] = useState([]);
  const [imagePath, setImagePath] = useState();
  const [uploading, setUploading] = useState(false);
  const [, setSelectedUsage] = useState(null);
  const [hasDefaultWarning, setHasDefaultWarning] = useState(false);
  const commonPaginationParams = `?page=${returnPage ?? 1}&pageSize=${returnPageSize ?? DATAGRID_DEFAULT_PAGE_SIZE
    }&statusType=${statusType ?? undefined}`;

  const deleteImage = async (imageId) => {
    try {
      // Note the capital 'I' in "Id" parameter
      const response = await axios.put(`${BASE_URL}${DELETE_WELCOME_IMAGE}?Id=${imageId}`);
      if (response?.data?.success) {
        navigate(
          `${RoutesLookup.SETTINGS}${commonPaginationParams}`
        );
        message.success(response?.data?.message || "Image deleted successfully");
        return;
      } else {
        message.error(response?.data?.errors || "Failed to delete image");
        return;
      }
    } catch (e) {
      console.log(e);
      message.error("An error occurred while deleting the image");
      return;
    }
  };

  const getImageById = async (imageId) => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_IMAGE_BY_ID}?Id=${imageId}`);
      const result = response?.data?.result;
      
      setImagePath(() => result?.GLIM_Image_Path);
      setSelectedUsage(result?.GLIM_Image_Usage_GLIU_KeyID);
      
      form.setFieldsValue({
        description: result?.GLIM_Description,
        image_usage: result?.GLIM_Image_Usage_GLIU_KeyID,
        image_path: result?.GLIM_Image_Path,
        is_default: result?.GLIM_Is_Default,
        start_date: result?.GLIM_Date_From ? dayjs(
          dateFormat(result?.GLIM_Date_From, "MM-DD-YYYY"),
          "MM-DD-YYYY"
        ) : null,
        end_date: result?.GLIM_Date_To ? dayjs(
          dateFormat(result?.GLIM_Date_To, "MM-DD-YYYY"),
          "MM-DD-YYYY"
        ) : null,
      });

      // Check if image is an end-dated default and show warning
      const now = new Date();
      const endDate = result?.GLIM_Date_To ? new Date(result?.GLIM_Date_To) : null;
      if (result?.GLIM_Is_Default && endDate && endDate < now) {
        setHasDefaultWarning(true);
      }
    } catch (e) {
      console.log(e);
      message.error("Failed to fetch image details");
    }
  };

  const getImageUsages = async () => {
    try {
      // Ensure this route is only called for admin users
      const response = await axios.get(`${BASE_URL}${GET_ALL_IMAGE_USAGES}`);
      
      // If we get here, the request succeeded
      if (response?.data?.success) {
        setImageUsages(response?.data?.result || []);
      }
    } catch (e) {
      console.error("GetAllImageUsages error:", e);
      // Check if the error is a 401
      if (e.response && e.response.status === 401) {
        message.error("You don't have permission to access image usages");
      } else {
        message.error("Failed to fetch image usages");
      }
    }
  };

  useEffect(() => {
    getImageUsages();
    if (id !== "create") {
      getImageById(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = (file) => {
    const fileExtension = file.name.split(".")[1];
    const isCorrectType =
        file.type === "image/jpeg" ||
        fileExtension === "jpeg" ||
        file.type === "image/png" ||
        fileExtension === "png" ||
        file.type === "image/jpg" ||
        fileExtension === "jpg";
    
    if (!isCorrectType) {
        message.error("You can only upload jpg/jpeg/png images!");
        return false;
    }
    
    const isCorrectSize = file.size / 1024 / 1024 <= 10;
    if (!isCorrectSize) {
        message.error("Image must be smaller than 10MB!");
        return false;
    }
    
    uploadImage(file);
    return false;
  };

  const uploadImage = async (file) => {
    setUploading(true);
    
    try {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async () => {
            const base64Data = reader.result.split(",")[1];
            const value = {
                Image_String: base64Data,
                Image_Name: file?.name,
            };
            
            try {
                const response = await axios.post(
                    `${BASE_URL}${UPLOAD_WELCOME_IMAGE}`,
                    value
                );
                
                if (response?.data) {
                    setImagePath(response.data);
                    form.setFieldsValue({ image_path: response.data });
                    message.success("Image uploaded successfully");
                }
            } catch (error) {
                console.error(error);
                message.error("Failed to upload image");
            } finally {
                setUploading(false);
            }
        };
    } catch (error) {
        console.error(error);
        message.error("Failed to process image");
        setUploading(false);
    }
  };

  const handleRemove = () => {
    setImagePath(undefined);
    form.setFieldsValue({ image_path: undefined });
  };

  const handleUsageChange = (value) => {
    setSelectedUsage(value);
  };

  const handleDefaultChange = (e) => {
    if (e.target.checked) {
      // Show a confirmation message
      message.info('Setting this image as default will remove default status from any other image with the same usage type.');
    }
  };

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className='edit-global-image'
      >
        <Row
          style={{
            position: "relative",
          }}
          gutter={{
            xs: 8,
            sm: 16,
            md: 24,
            lg: 32,
          }}
        >
          <Col className="gutter-row" span={12}>
            <Form.Item
              label="Description"
              name="description"
              rules={[
                { required: true, message: "Please enter a description" },
              ]}
            >
              <TextArea
                style={{ minHeight: 100 }}
                size="large"
                placeholder="Description"
              />
            </Form.Item>
            
            <Form.Item
              label="Image Usage"
              name="image_usage"
              rules={[{ required: true, message: "Select Image Usage" }]}
            >
              <Select
                showSearch
                placeholder="Select Usage"
                size="large"
                optionFilterProp="children"
                notFoundContent="No options found"
                onChange={handleUsageChange}
              >
                {imageUsages?.map((item) => (
                  <Option key={item?.GLIU_KeyID} value={item.GLIU_KeyID}>
                    {item.GLIU_Description}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            
            <Row
              gutter={{
                xs: 8,
                sm: 16,
                md: 24,
                lg: 32,
              }}
            >
              <Col className="gutter-row" span={12}>
                <Form.Item
                  label="Start date"
                  name="start_date"
                  rules={[{ required: true, message: "Please enter a date" }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="Select Date"
                    size="large"
                    format="MM-DD-YYYY"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={12}>
                <Form.Item
                  label="End date"
                  name="end_date"
                  rules={[
                    { required: false, message: "Please enter a date" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          value &&
                          new Date(getFieldValue("start_date")) <=
                          new Date(getFieldValue("end_date"))
                        ) {
                          return Promise.resolve();
                        }
                        if (!value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            "End Date must be greater than start date"
                          )
                        );
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="Select Date"
                    size="large"
                    format="MM-DD-YYYY"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="is_default"
              valuePropName="checked"
              style={{ marginTop: 20 }}
            >
              <Checkbox onChange={handleDefaultChange}>
                <span style={{ fontSize: 16 }}>Set as default image for this usage type</span>
              </Checkbox>
            </Form.Item>

            {hasDefaultWarning && (
              <Alert
                message="Warning: default image date misleading"
                description="This Image will be set as default, but it is passed its end date, the image will be active which is misleading, consider changing the image date"
                type="warning"
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}
            
            <Row>
              <Col
                span={24}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 30,
                }}
              >
                <div></div>
                <Space direction="horizontal" size={20}>
                  {id !== "create" && (
                    <Popconfirm
                      title="Are you sure you want to delete this image?"
                      okText="Yes"
                      cancelText="No"
                      onConfirm={() => deleteImage(id)}
                    >
                      <Button
                        style={{ color: colors.red }}
                        type="default"
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                  )}
                  <Button
                    style={{ color: colors.black }}
                    type="default"
                    onClick={async () => {
                      form.resetFields();
                      navigate(
                        `${RoutesLookup.SETTINGS}${commonPaginationParams}`
                      );
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                  >
                    {id !== "create" ? "Save Changes" : "Add Image"}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Col>
          <Col className="gutter-row" span={12}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Row style={{ width: "100%" }}>
                <Col span={24}>
                  <div
                    style={{
                      width: "100%",
                      marginBottom: 24,
                    }}
                  >
                    <Form.Item 
                      label="Image"
                      name="image_path" 
                      rules={[
                        { required: true, message: "Please upload an image" },
                      ]}
                    >
                      <div>
                        {imagePath ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Image
                                    src={imagePath}
                                    alt="Global Image"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        objectFit: 'contain',
                                        marginBottom: 16
                                    }}
                                />
                                <Button 
                                    onClick={handleRemove}
                                    type="default"
                                    style={{ marginTop: 8 }}
                                >
                                    Remove Image
                                </Button>
                            </div>
                        ) : (
                            <Dragger
                                multiple={false}
                                showUploadList={false}
                                style={{
                                    border: "1px dashed #d9d9d7",
                                    borderRadius: 8,
                                    backgroundColor: colors.white,
                                    padding: 20
                                }}
                                beforeUpload={handleFileUpload}
                            >
                                <div>
                                    {uploading ? <LoadingOutlined style={{ fontSize: 36 }} /> : (
                                        <Image
                                            src={uploadIcon}
                                            alt="upload"
                                            preview={false}
                                            style={{ objectFit: "cover" }}
                                            width={36}
                                            height={36}
                                        />
                                    )}
                                    <div style={{ marginTop: 10 }}>
                                        <Text 
                                            className="ant-upload-text" 
                                            style={{
                                                marginRight: 5,
                                                color: colors.secondaryBlue,
                                                fontSize: 16
                                            }}
                                        >
                                            Upload an image
                                        </Text>
                                        <Text 
                                            className="ant-upload-text" 
                                            style={{
                                                fontSize: 16
                                            }}
                                        >
                                            or drag and drop
                                        </Text>
                                    </div>
                                    <Text className="ant-upload-hint">
                                        PNG, JPG, up to 10MB
                                    </Text>
                                </div>
                            </Dragger>
                        )}
                      </div>
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Form>
    </Spin>
  );
};

export default GlobalImageForm;